import React, { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield, Code2, ArrowRight, MessageCircle, Star, Award, Youtube, Zap, Clock,
  Users, Trophy, Briefcase, Code, CheckCircle, Building2, TrendingUp, Target, Rocket, Play, Newspaper, Tv
} from 'lucide-react';
import { courses } from '@/data/coursesData';
import CourseCard from '@/components/CourseCard';
import { podcastsData, mediaCoverageData } from '@/data/mediaData';
import FAQSection from '@/components/FAQSection';
import LocalJaipurSection from '@/components/LocalJaipurSection';

const Hero = () => {
  const stats = [
    { icon: Shield, label: "Instagram Followers", value: "340K+", color: "from-pink-500 to-red-500" },
    { icon: Youtube, label: "YouTube Subscribers", value: "80K+", color: "from-red-500 to-red-600" },
    { icon: Award, label: "Students Trained", value: "30K+", color: "from-orange-500 to-red-500" },
    { icon: Star, label: "Success Rate", value: "95%", color: "from-yellow-500 to-orange-500" }
  ];

  const handleWhatsApp = () => {
    window.open('https://wa.me/919694612895', '_blank');
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-950 via-black to-gray-950 pt-14 sm:pt-16 md:pt-18">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(220,38,38,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(220,38,38,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 -left-32 w-64 h-64 sm:w-96 sm:h-96 bg-red-600 rounded-full filter blur-[96px] sm:blur-[128px] animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-32 w-64 h-64 sm:w-96 sm:h-96 bg-orange-600 rounded-full filter blur-[96px] sm:blur-[128px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
          <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600/10 to-orange-600/10 border border-red-500/20 rounded-full px-3 sm:px-6 py-2 sm:py-3 backdrop-blur-sm mx-auto lg:mx-0">
              <Shield className="w-3 h-3 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" />
              <span className="text-[10px] sm:text-sm font-semibold text-gray-200 tracking-wide">India's Best & Most Followed Cyber Academy</span>
              <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-orange-500 animate-pulse flex-shrink-0" />
            </div>

            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.1] tracking-tight">
                Master
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-red-500">
                  Ethical Hacking
                </span>
                <span className="block mt-2 text-gray-300 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold">
                  & Cybersecurity
                </span>
              </h1>
              <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-red-600 to-orange-600 rounded-full mx-auto lg:mx-0"></div>
            </div>

            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 leading-relaxed max-w-xl mx-auto lg:mx-0 px-4 sm:px-0">
              Join <span className="text-white font-bold">340K+ cybersecurity enthusiasts</span>. Learn from <span className="text-white font-semibold">certified industry experts</span> with hands-on training.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 justify-center lg:justify-start px-4 sm:px-0">
              <Button
                size="lg"
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-sm sm:text-base md:text-lg px-6 sm:px-10 py-5 sm:py-7 shadow-xl shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300 font-semibold group touch-manipulation w-full sm:w-auto active:scale-95"
                onClick={() => window.location.href = '#courses'}
              >
                Explore Courses
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-gray-700 text-gray-200 hover:border-red-500 hover:bg-red-500/10 text-sm sm:text-base md:text-lg px-6 sm:px-10 py-5 sm:py-7 transition-all duration-300 bg-transparent font-semibold touch-manipulation w-full sm:w-auto active:scale-95"
                onClick={handleWhatsApp}
              >
                <Code2 className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Talk to Expert
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-6 sm:pt-8 max-w-2xl mx-auto lg:mx-0">
              {stats.map((stat, index) => (
                <div key={index} className="relative group">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 rounded-xl sm:rounded-2xl blur-xl transition-all duration-300`}></div>
                  <div className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-800 hover:border-red-500/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 transition-all duration-300 touch-manipulation">
                    <stat.icon className="w-4 h-4 sm:w-6 sm:h-6 text-red-500 mb-2 sm:mb-3" />
                    <div className="text-lg sm:text-2xl md:text-3xl font-black text-white mb-1 break-words">{stat.value}</div>
                    <div className="text-[9px] sm:text-xs text-gray-400 font-medium break-words leading-tight">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative lg:ml-8 mt-8 lg:mt-0 px-4 sm:px-0">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-2xl sm:rounded-3xl blur-2xl"></div>
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden border border-gray-800 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1200"
                  alt="Ethical Hacking Training"
                  className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[550px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>

                <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8">
                  <div className="bg-black/80 backdrop-blur-xl border border-red-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] sm:text-sm text-red-500 font-semibold mb-1 tracking-wide">CERTIFIED TRAINING</div>
                        <div className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-1">30,000+ Students</div>
                        <div className="text-xs sm:text-sm text-gray-400">Trained Nationwide</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBadges = () => {
  const badges = [
    { icon: Target, text: "100% Practical Training", color: "from-red-500 to-orange-500" },
    { icon: Award, text: "Industry Certified", color: "from-purple-500 to-pink-500" },
    { icon: Users, text: "340K+ Community", color: "from-blue-500 to-cyan-500" },
    { icon: Rocket, text: "Placement Support", color: "from-green-500 to-emerald-500" }
  ];

  return (
    <section className="py-8 sm:py-12 bg-gray-950 border-y border-gray-900">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {badges.map((badge, index) => (
            <div key={index} className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center sm:text-left p-3 sm:p-4 rounded-lg bg-gray-900/50 border border-gray-800 hover:border-red-500/30 transition-all duration-300">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br ${badge.color} flex items-center justify-center flex-shrink-0`}>
                <badge.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-semibold text-gray-300 leading-tight break-words">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CoursesSection = () => {
  return (
    <section id="courses" className="py-16 sm:py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-red-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Code2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">Our Courses</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mt-4 mb-4 sm:mb-6 px-4">
            Industry-Ready{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
              Cybersecurity Courses
            </span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg px-4">
            Choose from live instructor-led training, self-paced courses, or comprehensive Advance Training programs
          </p>
        </div>

        <Tabs defaultValue="live" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-8 sm:mb-12 bg-gray-900/50 border border-gray-800">
            <TabsTrigger value="live" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              Live Courses
            </TabsTrigger>
            <TabsTrigger value="selfPaced" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              Self-Paced
            </TabsTrigger>
            <TabsTrigger value="Advance-Training" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm">
              Advance Training
            </TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="mt-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {courses.live.map((course) => (
                <CourseCard key={course.id} course={course} type="live" />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="selfPaced" className="mt-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {courses.selfPaced.map((course) => (
                <CourseCard key={course.id} course={course} type="selfPaced" />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="Advance-Training" className="mt-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {courses.Advance-Training.map((course) => (
                <CourseCard key={course.id} course={course} type="Advance-Training" />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const WhyChooseUs = () => {
  const features = [
    { icon: Shield, title: "Expert Instructors", desc: "Learn from certified professionals" },
    { icon: Code, title: "Hands-on Labs", desc: "30+ days lab environment" },
    { icon: Briefcase, title: "Placement Support", desc: "Resume & interview prep" },
    { icon: Trophy, title: "Certifications", desc: "Industry recognized certificates" }
  ];

  return (
    <section className="py-16 sm:py-20 bg-gray-900 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 px-4">
            Why <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Choose Us</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base px-4">World-class cybersecurity education with practical experience</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <div key={index} className="bg-gray-950/50 border border-gray-800 rounded-xl p-4 sm:p-6 hover:border-red-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-red-600 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);

  const testimonials = [
    { name: 'Rajesh Kumar', role: 'SOC Analyst at TCS', text: 'Exceptional training. The hands-on approach helped me land my dream job in cybersecurity.', initial: 'RK', color: 'from-blue-500 to-cyan-500' },
    { name: 'Priya Sharma', role: 'Penetration Tester', text: 'Bug Bounty course was game-changing. Now earning consistently from bug bounty programs!', initial: 'PS', color: 'from-pink-500 to-rose-500' },
    { name: 'Amit Verma', role: 'Security Engineer at Infosys', text: 'Comprehensive curriculum and practical labs. Placement support was excellent!', initial: 'AV', color: 'from-purple-500 to-indigo-500' },
    { name: 'Sneha Patel', role: 'Digital Forensics Expert', text: 'Forensics course covered everything. Now working with law enforcement agencies!', initial: 'SP', color: 'from-green-500 to-emerald-500' },
    { name: 'Vikram Singh', role: 'Ethical Hacker', text: 'Best ethical hacking training in India. Started my own consulting firm!', initial: 'VS', color: 'from-orange-500 to-red-500' },
    { name: 'Kavya Iyer', role: 'Bug Bounty Hunter', text: 'From zero to earning from bug bounties in 6 months! Practical approach worked!', initial: 'KI', color: 'from-yellow-500 to-orange-500' }
  ];

  useEffect(() => {
    const scroll1 = scrollRef1.current;
    const scroll2 = scrollRef2.current;
    
    let scrollPos1 = 0;
    let scrollPos2 = 0;

    const animate1 = () => {
      if (scroll1) {
        scrollPos1 += 0.5;
        if (scrollPos1 >= scroll1.scrollWidth / 2) {
          scrollPos1 = 0;
        }
        scroll1.scrollLeft = scrollPos1;
      }
    };

    const animate2 = () => {
      if (scroll2) {
        scrollPos2 -= 0.5;
        if (scrollPos2 <= 0) {
          scrollPos2 = scroll2.scrollWidth / 2;
        }
        scroll2.scrollLeft = scrollPos2;
      }
    };

    const interval1 = setInterval(animate1, 20);
    const interval2 = setInterval(animate2, 20);

    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, []);

  const TestimonialCard = ({ testimonial }) => (
    <div className="flex-shrink-0 w-80 sm:w-96 bg-gradient-to-br from-gray-900 to-gray-950 border border-red-600/30 rounded-xl p-4 sm:p-6 mx-3 hover:border-red-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/10">
      <div className="flex mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-500 text-yellow-500" />
        ))}
      </div>
      <p className="text-xs sm:text-sm text-gray-300 leading-relaxed mb-4">"{testimonial.text}"</p>
      <div className="pt-4 border-t border-gray-800">
        <div className="font-bold text-white text-sm sm:text-base">{testimonial.name}</div>
        <div className="text-xs sm:text-sm text-red-500">{testimonial.role}</div>
      </div>
    </div>
  );

  return (
    <section className="py-16 sm:py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">Testimonials</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 px-4">
            What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Students</span> Say
          </h2>
          <p className="text-gray-400 text-sm sm:text-base px-4">
            Trusted by <span className="text-red-500 font-bold">30,000+</span> students who transformed their careers
          </p>
        </div>

        <div className="mb-6 sm:mb-8 relative">
          <div ref={scrollRef1} className="flex overflow-x-hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex">
              {[...testimonials, ...testimonials].map((t, i) => (
                <TestimonialCard key={`r1-${i}`} testimonial={t} />
              ))}
            </div>
          </div>
        </div>

        <div className="relative">
          <div ref={scrollRef2} className="flex overflow-x-hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex">
              {[...testimonials.reverse(), ...testimonials].map((t, i) => (
                <TestimonialCard key={`r2-${i}`} testimonial={t} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-gray-950/80 border border-red-600/30 rounded-xl px-6 sm:px-8 py-3 sm:py-4">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <span className="text-white font-bold text-sm sm:text-base">4.9/5</span>
            </div>
            <div className="h-6 sm:h-8 w-px bg-red-600/30"></div>
            <div className="text-gray-400 text-xs sm:text-sm">
              Based on <span className="text-white font-bold">5000+</span> reviews
            </div>
          </div>
        </div>
      </div>

      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

const CompaniesSection = () => {
  const scrollRef = useRef(null);

  const companies = [
    { name: 'TCS' }, { name: 'Infosys' }, { name: 'Wipro' },
    { name: 'Cognizant' }, { name: 'HCL' }, { name: 'Accenture' },
    { name: 'IBM' }, { name: 'Amazon' }, { name: 'Microsoft' }, { name: 'Google' }
  ];

  useEffect(() => {
    const scroll = scrollRef.current;
    let scrollPos = 0;

    const animate = () => {
      if (scroll) {
        scrollPos += 0.5;
        if (scrollPos >= scroll.scrollWidth / 2) {
          scrollPos = 0;
        }
        scroll.scrollLeft = scrollPos;
      }
    };

    const interval = setInterval(animate, 20);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 sm:py-20 bg-gray-950 border-y border-gray-900 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">Placement Partners</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-4 px-4">
            Our Students Work At <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Top Companies</span>
          </h2>
        </div>

        <div className="relative mb-12">
          <div ref={scrollRef} className="flex overflow-x-hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex">
              {[...companies, ...companies, ...companies].map((company, index) => (
                <div key={`c-${index}`} className="flex-shrink-0 mx-4 sm:mx-6">
                  <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-8 w-32 h-20 sm:w-48 sm:h-32 flex items-center justify-center hover:scale-105 transition-transform border-2 border-transparent hover:border-red-500/50">
                    <span className="text-gray-800 font-bold text-base sm:text-xl">{company.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
          <div className="bg-gray-900/50 border border-red-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-red-500 mb-1 sm:mb-2">30K+</div>
            <div className="text-xs sm:text-sm text-gray-400">Students Trained</div>
          </div>
          <div className="bg-gray-900/50 border border-red-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-red-500 mb-1 sm:mb-2">500+</div>
            <div className="text-xs sm:text-sm text-gray-400">Hiring Partners</div>
          </div>
          <div className="bg-gray-900/50 border border-red-600/30 rounded-lg sm:rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-red-500 mb-1 sm:mb-2">12L</div>
            <div className="text-xs sm:text-sm text-gray-400">Avg. Package</div>
          </div>
        </div>
      </div>

      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

const CTASection = () => {
  return (
    <section className="py-16 sm:py-24 bg-gray-900 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gray-950 to-gray-900 border-2 border-red-500/30 rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center shadow-2xl shadow-red-500/20">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-bold border border-red-500/50">
                LIMITED SEATS AVAILABLE
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 px-4">
              Ready to Become an{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                Elite Ethical Hacker?
              </span>
            </h2>

            <p className="text-sm sm:text-base lg:text-xl text-gray-400 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
              Join <span className="text-red-500 font-bold">340K+</span> elite hackers. Master penetration testing & get certified!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-sm sm:text-base lg:text-lg px-8 sm:px-10 py-5 sm:py-7 shadow-xl shadow-red-500/25 w-full sm:w-auto"
                onClick={() => window.location.href = '#courses'}
              >
                Browse All Courses
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-sm sm:text-base lg:text-lg px-8 sm:px-10 py-5 sm:py-7 bg-transparent w-full sm:w-auto"
                onClick={() => window.open('https://wa.me/919694612895', '_blank')}
              >
                <MessageCircle className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturedPodcasts = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scroll = scrollRef.current;
    let scrollPos = 0;

    const animate = () => {
      if (scroll) {
        scrollPos += 0.5;
        if (scrollPos >= scroll.scrollWidth / 2) {
          scrollPos = 0;
        }
        scroll.scrollLeft = scrollPos;
      }
    };

    const interval = setInterval(animate, 20);
    return () => clearInterval(interval);
  }, []);

  const PodcastCard = ({ podcast }) => (
    <a
      href={podcast.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex-shrink-0 w-64 sm:w-72 md:w-80 mx-2 sm:mx-3 group relative bg-gray-900/50 backdrop-blur-sm border-2 border-gray-800 rounded-lg sm:rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/20 hover:-translate-y-2"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={podcast.thumbnail}
          alt={podcast.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.src = 'https://img.youtube.com/vi/default/maxresdefault.jpg';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-600 rounded-full flex items-center justify-center shadow-2xl shadow-red-500/50 group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white ml-1" fill="white" />
          </div>
        </div>

        {/* Platform Badge */}
        <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1">
          <Youtube className="w-3 h-3" />
          {podcast.platform}
        </div>
      </div>

      <div className="p-3 sm:p-4">
        <h3 className="text-sm sm:text-base font-bold text-white line-clamp-2 group-hover:text-red-500 transition-colors break-words leading-tight">
          {podcast.title}
        </h3>
        {podcast.description && (
          <p className="text-xs text-gray-400 line-clamp-1 break-words mt-1">{podcast.description}</p>
        )}
      </div>
    </a>
  );

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-br from-gray-950 via-gray-900 to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-red-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <Play className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">Featured Podcasts & Interviews</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4 px-4">
            Learn From <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Abhishek Parashar</span>
          </h2>
          <p className="text-xs sm:text-sm lg:text-base text-gray-400 px-4">
            India's leading cybersecurity expert featured on major platforms
          </p>
        </div>

        {/* Single Carousel */}
        <div className="relative mb-8 sm:mb-12">
          <div 
            ref={scrollRef}
            className="flex overflow-x-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex">
              {[...podcastsData, ...podcastsData].map((podcast, index) => (
                <PodcastCard key={`podcast-${index}`} podcast={podcast} />
              ))}
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <a
            href="https://www.youtube.com/@AbhishekParashar-ICC"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 sm:gap-3 bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:scale-105 w-full sm:w-auto justify-center"
          >
            <Youtube className="w-4 h-4 sm:w-5 sm:h-5" />
            <div className="text-left">
              <div className="text-[10px] font-medium opacity-90">Subscribe on</div>
              <div className="text-xs sm:text-sm font-bold">YouTube - 80K+</div>
            </div>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
          </a>

          <a
            href="https://www.instagram.com/indian_cyber_club/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 sm:gap-3 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 hover:from-purple-700 hover:via-pink-700 hover:to-orange-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all duration-300 shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-105 w-full sm:w-auto justify-center"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <div className="text-left">
              <div className="text-[10px] font-medium opacity-90">Follow on</div>
              <div className="text-xs sm:text-sm font-bold">Instagram - 340K+</div>
            </div>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Gradient Overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

const MediaCoverage = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scroll = scrollRef.current;
    let scrollPos = 0;

    const animate = () => {
      if (scroll) {
        scrollPos += 0.5;
        if (scrollPos >= scroll.scrollWidth / 2) {
          scrollPos = 0;
        }
        scroll.scrollLeft = scrollPos;
      }
    };

    const interval = setInterval(animate, 20);
    return () => clearInterval(interval);
  }, []);

  const MediaCard = ({ media }) => (
    <div className="flex-shrink-0 w-48 sm:w-56 md:w-64 mx-2 sm:mx-3 group relative bg-gradient-to-br from-gray-900 to-gray-950 backdrop-blur-sm border-2 border-gray-800 rounded-lg sm:rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-500/20 hover:-translate-y-2">
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-800">
        <img
          src={media.image}
          alt={media.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
        
        {/* Category Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow-lg ${
          media.category === 'Press' ? 'bg-red-600 text-white' :
          media.category === 'Academic' ? 'bg-blue-600 text-white' :
          media.category === 'Law Enforcement' ? 'bg-green-600 text-white' :
          media.category === 'Training' ? 'bg-purple-600 text-white' :
          'bg-orange-600 text-white'
        }`}>
          {media.category === 'Press' && <Newspaper className="w-2.5 h-2.5" />}
          {media.category === 'Academic' && <Award className="w-2.5 h-2.5" />}
          {media.category === 'Law Enforcement' && <Shield className="w-2.5 h-2.5" />}
          {media.category === 'Training' && <Users className="w-2.5 h-2.5" />}
          {media.category === 'Awareness' && <TrendingUp className="w-2.5 h-2.5" />}
          <span className="hidden sm:inline">{media.category}</span>
        </div>

        {/* Verified Badge */}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
          <CheckCircle className="w-2.5 h-2.5 text-green-600" />
          <span className="text-[9px] font-bold text-gray-900 hidden sm:inline">Verified</span>
        </div>
      </div>

      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] text-red-500 font-bold uppercase truncate">{media.source}</span>
          <span className="text-[10px] text-gray-600">•</span>
          <span className="text-[10px] text-gray-500">{media.date}</span>
        </div>
        <h3 className="text-xs sm:text-sm font-bold text-white line-clamp-2 group-hover:text-red-500 transition-colors break-words leading-tight">
          {media.title}
        </h3>
      </div>

      {/* Shine Effect on Hover */}
      <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none"></div>
    </div>
  );

  return (
    <section className="py-16 sm:py-20 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-orange-600 rounded-full filter blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <Newspaper className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-xs sm:text-sm uppercase tracking-wider">Media Coverage & Recognition</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 sm:mb-4 px-4">
            Featured in <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">National Media</span> & Institutions
          </h2>
          <p className="text-xs sm:text-sm lg:text-base text-gray-400 px-4">
            Recognized by leading newspapers, universities, and law enforcement agencies
          </p>
        </div>

        {/* Single Carousel */}
        <div className="relative mb-8 sm:mb-12">
          <div 
            ref={scrollRef}
            className="flex overflow-x-hidden scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex">
              {[...mediaCoverageData, ...mediaCoverageData].map((media, index) => (
                <MediaCard key={`media-${index}`} media={media} />
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Recognition Stats */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 border-2 border-red-500/30 rounded-xl sm:rounded-2xl p-6 sm:p-8 lg:p-10">
            <h3 className="text-xl sm:text-2xl font-black text-white text-center mb-6 sm:mb-8">
              Recognition & Impact
            </h3>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative bg-gray-900 border border-red-600/30 rounded-lg p-3 sm:p-4 text-center hover:border-red-500 transition-all hover:-translate-y-1">
                  <Tv className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-1">10+</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 font-semibold">TV Interviews</div>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative bg-gray-900 border border-red-600/30 rounded-lg p-3 sm:p-4 text-center hover:border-red-500 transition-all hover:-translate-y-1">
                  <Newspaper className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-1">25+</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 font-semibold">Press Features</div>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative bg-gray-900 border border-red-600/30 rounded-lg p-3 sm:p-4 text-center hover:border-red-500 transition-all hover:-translate-y-1">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-1">30+</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 font-semibold">University Talks</div>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                <div className="relative bg-gray-900 border border-red-600/30 rounded-lg p-3 sm:p-4 text-center hover:border-red-500 transition-all hover:-translate-y-1">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-red-500 mx-auto mb-2" />
                  <div className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-1">15+</div>
                  <div className="text-[10px] sm:text-xs text-gray-400 font-semibold">Cyber Cell Collab</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gradient Overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
    </section>
  );
};

const HomePage = () => {
  return (
    <>
      <Hero />
      <TrustBadges />
      <FeaturedPodcasts />
      <CoursesSection />
      <MediaCoverage />
      <WhyChooseUs />
      <TestimonialsSection />
      <CompaniesSection />
      <LocalJaipurSection />
      <FAQSection />
      <CTASection />
    </>
  );
};

export default HomePage;
